import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Trans, useTranslation } from 'react-i18next'
import classnames from 'classnames'
import { toast, ToastContainer } from 'react-toastify'
import PropTypes from 'prop-types'

import { setRequestInformationModalOpen } from '../../reducers/communityLandingPageReducer'
import { getCommunityTimeInNetwork } from '../../utility/datesHelper'
import { canContactCommunity } from '../../utility/userHelper'
import CollapsibleBar from './collapsible-bar/CollapsibleBar'

import ambassadorBadge from '../../assets/images/pins/ambassador_badge.png'
import certifiedBadge from '../../assets/images/pins/certified_badge.png'
import top50Badge from '../../assets/images/pins/top_50_generic.png'
import masterTop50Badge from '../../assets/images/pins/master_top_50.png'
import { ReactComponent as QuestionMark } from '../../assets/images/common/icons/question-mark-circle.svg'
import { ReactComponent as CallIcon } from '../../assets/images/common/icons/call-icon.svg'
import { ReactComponent as ShareIcon } from '../../assets/images/common/icons/share-icon.svg'
import { ReactComponent as CalendarIcon } from '../../assets/images/common/icons/communityLandingPage/calendar-icon.svg'
import { ReactComponent as GolferIcon } from '../../assets/images/common/icons/communityLandingPage/golfer-icon.svg'
import { ReactComponent as StudentIcon } from '../../assets/images/common/icons/communityLandingPage/student-icon.svg'
import defaultAvatar from '../../assets/images/mock/default-profile-icon.png'
import classes from './CommunityDetails.module.scss'
import CoachCollapsibleInfo from './collapsible-bar/CoachCollapsibleInfo'


const collapsibleBarClassnames = {
  collapsibleBar: classes.collapsibleBar,
  bar: classes.bar,
  collapsibleBarExpanded: classes.collapsibleBarExpanded,
}

const CommunityDetails = ({ className }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const community = useSelector(state => state.communityLandingPage.community)
  const user = useSelector(state => state.user.current_user)
  const coaches = community.coaches

  const { period, count } = useMemo(() => getCommunityTimeInNetwork(community.networkDate || community.createdAt), [community])
  const landingPageUrl = `${process.env.REACT_APP_BASE_URL}/landing/${community.id}`

  const handleShareButtonClick = () => {
    navigator.clipboard.writeText(landingPageUrl)
      .then(() => {
        toast.info(t('communityLanding.details.linkCopied'), {
          toastId: 'LinkCopiedToast',
          position: 'bottom-right',
          autoClose: 2500,
        })
      })
  }

  return (
    <div className={classnames('flex-column p-4', className, classes.communityDetails)}>
      <p className={classnames('mb-4', classes.description)}>{community.description}</p>
      {canContactCommunity(user, community.id) && (
        <button
          className={classnames('animation-shrink d-flex align-items-center', classes.requestInfoButton)}
          onClick={() => dispatch(setRequestInformationModalOpen(true))}
        >
          <QuestionMark className={classnames('flex-shrink-0', classes.questionMark)} />
          <span className='ml-3'>{t('communityLanding.details.requestInfo')}</span>
        </button>
      )}
      <div className={classnames('d-flex px-3', community.phone ? 'justify-content-between' : 'justify-content-center')}>
        {!!community.phone && (
          <a className={classnames('animation-shrink', classes.actionButton)} href={`tel:${community.phonePrefix || ''}${community.phone}`}>
            <CallIcon className={classes.actionIcon} />
            {t('communityLanding.details.callUs')}
          </a>
        )}
        <button className={classnames('animation-shrink', classes.actionButton)} onClick={handleShareButtonClick}>
          <ShareIcon className={classes.actionIcon} />
          {t('communityLanding.details.sharePage')}
        </button>
      </div>
      <ul className='p-0'>
        {coaches.length > 0 && (
          <CollapsibleBar
            components={{
              bar: <h2 className={classes.barTitle}>{t('communityLanding.details.coaches')}</h2>,
              collapse: (
                <ul className='p-0'>
                  {coaches.map(coach => (
                    <CollapsibleBar
                      key={coach.id}
                      classNames={{
                        collapsibleBar: classes.coachCollapsibleBar,
                        bar: classes.coachBar,
                      }}
                      components={{
                        bar: (
                          <div className='d-flex align-items-center'>
                            <div className='medium-round-image-wrapper flex-shrink-0 mr-2'>
                              <img
                                className='round-image'
                                src={coach.avatar ? coach.avatar.smallFileUrl : defaultAvatar}
                                alt={t('communityLanding.details.coachAvatarAlt', { name: `${coach.firstName} ${coach.lastName}` })}
                              />
                            </div>
                            <span className={classes.coachName}>{coach.firstName} {coach.lastName}</span>
                            {community.leadCoachId === coach.id && (
                              <span className={classes.leadCoach}>{t('communityLanding.details.leadCoach')}</span>
                            )}
                          </div>
                        ),
                        collapse: <CoachCollapsibleInfo coach={coach} />,
                      }}
                    />
                  ))}
                </ul>
              ),
            }}
            classNames={collapsibleBarClassnames}
          />
        )}
        <CollapsibleBar
          components={{
            bar: <h2 className={classes.barTitle}>{t('communityLanding.details.achievements')}</h2>,
            collapse: (
              <div className={classes.achievements}>
                <div className={classnames('mt-3', classes.statistics)}>
                  <CalendarIcon className={classes.icon}/>
                  <span className={classes.statisticsDescription}>
                    <Trans
                      i18nKey={`communityLanding.details.timeInNetwork.${period}`}
                      count={count}
                      components={{ num: <span className={classes.number} /> }}
                    >
                      <num>{{ count }}</num> Years in Network
                    </Trans>
                  </span>
                </div>
                <div className={classes.statistics}>
                  <StudentIcon />
                  <span className={classes.statisticsDescription}>
                    <Trans
                      i18nKey='communityLanding.details.totalStudents'
                      components={{ num: <span className={classes.number} /> }}
                    >
                      <num>{{ count: community.studentCount }}</num> Total Students
                    </Trans>
                  </span>
                </div>
                <div className={classes.statistics}>
                  <GolferIcon />
                  <span className={classes.statisticsDescription}>
                    <Trans
                      i18nKey='communityLanding.details.golfersCreated'
                      components={{ num: <span className={classes.number} /> }}
                    >
                      <num>{{ count: community.golfersCreated }}</num> Golfers Created
                    </Trans>
                  </span>
                </div>
                <div className={classes.accolades}>
                  {community.isAmbassador && (
                    <img src={ambassadorBadge} className={classes.accolade} alt='ambassador badge' />
                  )}
                  {community.isCertified && (
                    <img src={certifiedBadge} className={classes.accolade} alt={t('communityLanding.details.certifiedBadgeAlt')} />
                  )}
                  {community.isTop50 && (
                    <img src={top50Badge} className={classes.accolade} alt={t('communityLanding.details.topFiftyBadgeAlt')} />
                  )}
                  {community.isMasterTop50 && (
                    <img src={masterTop50Badge} className={classes.accolade} alt={t('communityLanding.details.masterTopFiftyBadgeAlt')} />
                  )}
                </div>
              </div>
            ),
          }}
          classNames={collapsibleBarClassnames}
        />
      </ul>
      <ToastContainer />
    </div>
  )
}

CommunityDetails.propTypes = {
  className: PropTypes.string,
}

export default CommunityDetails
